import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import Button from 'components/Shared/Button'
import { GoogleAuthButton } from 'components/Shared/GoogeAuthButton'
import Modal from 'components/Shared/Modal'
import { linkGoogleAccount, unlinkGoogleAccount } from 'utils/io'

import type { User } from 'ducks/users/index'

const LINK_ERROR_MESSAGES: Record<string, string> = {
  GOOGLE_ALREADY_LINKED: 'Google is already linked to this account.',
  GOOGLE_EMAIL_UNVERIFIED:
    'Your Google account email must be verified before connecting.',
  GOOGLE_EMAIL_MISMATCH:
    'Sign in with the Google account that uses the same email as your Adalo profile.',
  GOOGLE_ACCOUNT_IN_USE:
    'This Google account is already linked to a different Adalo user.',
  USER_NOT_FOUND: 'Something went wrong. Please refresh and try again.',
  SERVER_ERROR: 'Could not connect Google. Please try again.',
  UNKNOWN: 'Could not connect Google. Please try again.',
}

const UNLINK_ERROR_MESSAGES: Record<string, string> = {
  GOOGLE_NOT_LINKED: 'Google is not connected to this account.',
  PASSWORD_REQUIRED_TO_UNLINK:
    'Set a password for this account before disconnecting Google, so you can still sign in.',
  USER_NOT_FOUND: 'Something went wrong. Please refresh and try again.',
  SERVER_ERROR: 'Could not disconnect Google. Please try again.',
  UNKNOWN: 'Could not disconnect Google. Please try again.',
}

type AccountGoogleConnectionProps = {
  user: User
  /** Profile tab URL (set password) — include app context in the editor, e.g. `/apps/:id/screens/settings/account`. */
  profileSettingsPath?: string
}

export function AccountGoogleConnection({
  user,
  profileSettingsPath = '/settings/account',
}: AccountGoogleConnectionProps): JSX.Element {
  const googleLinked = user.googleId != null && user.googleId !== ''
  const passwordRequiredToDisconnect = user.hasPassword === false

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  useEffect(() => {
    if (!success) {
      return
    }

    const t = window.setTimeout(() => setSuccess(null), 5000)

    return () => window.clearTimeout(t)
  }, [success])

  const handleLink = useCallback(async (idToken: string | undefined) => {
    if (!idToken) return
    setError(null)
    setSuccess(null)
    setBusy(true)
    try {
      await linkGoogleAccount({ idToken })
      setSuccess('Google account connected.')
    } catch (res: unknown) {
      const code =
        res && typeof res === 'object' && 'error' in res
          ? String((res as { error?: string }).error)
          : 'UNKNOWN'
      setError(
        LINK_ERROR_MESSAGES[code] ??
          LINK_ERROR_MESSAGES['UNKNOWN'] ??
          'Could not connect Google. Please try again.'
      )
    } finally {
      setBusy(false)
    }
  }, [])

  const handleUnlink = useCallback(async () => {
    if (passwordRequiredToDisconnect) {
      return
    }
    setError(null)
    setSuccess(null)
    setBusy(true)
    try {
      await unlinkGoogleAccount()
      setSuccess('Google account disconnected.')
    } catch (res: unknown) {
      const code =
        res && typeof res === 'object' && 'error' in res
          ? String((res as { error?: string }).error)
          : 'UNKNOWN'
      setError(
        UNLINK_ERROR_MESSAGES[code] ??
          UNLINK_ERROR_MESSAGES['UNKNOWN'] ??
          'Could not disconnect Google. Please try again.'
      )
    } finally {
      setBusy(false)
    }
  }, [passwordRequiredToDisconnect])

  return (
    <>
      <h3 className="settings-header">Google sign-in</h3>
      <div className="settings-content-box">
        {success ? (
          <div className="settings-form-alert settings-save-success">
            {success}
          </div>
        ) : null}
        {error ? <div className="settings-form-alert">{error}</div> : null}
        <div className="settings-content-row">
          <p className="settings-content-label">Status</p>
          <h4>{googleLinked ? 'Connected' : 'Not connected'}</h4>
          <p
            style={{
              color: '#666',
              fontSize: 14,
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            {googleLinked
              ? 'You can sign in to Adalo with Google. Your Google email matches this Adalo account.'
              : 'Connect Google to sign in with one click. Your Google account must use the same verified email as this profile.'}
          </p>
        </div>
        <div
          className="setting-modal-button-row"
          style={{
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px 16px',
          }}
        >
          {!googleLinked ? (
            <GoogleAuthButton
              inProgress={busy}
              onClick={credential => {
                handleLink(credential).catch(() => {
                  /* handled inside handleLink */
                })
              }}
            />
          ) : (
            <>
              <Button
                small
                outlined
                teal
                disabled={busy || passwordRequiredToDisconnect}
                loading={busy}
                title={
                  passwordRequiredToDisconnect
                    ? 'Set a password on your profile before disconnecting Google.'
                    : undefined
                }
                onClick={() => {
                  setShowDisconnectConfirm(true)
                }}
              >
                Disconnect Google
              </Button>
              {passwordRequiredToDisconnect ? (
                <span style={{ color: '#666', fontSize: 14 }}>
                  <Link to={profileSettingsPath} className="link-button">
                    Set up a password
                  </Link>{' '}
                  on Profile first.
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
      {showDisconnectConfirm ? (
        <Modal
          size="sm"
          showClose
          onClose={() => setShowDisconnectConfirm(false)}
        >
          <Modal.Header>Disconnect Google?</Modal.Header>
          <Modal.Content>
            Disconnect Google from this Adalo account? You will sign in with
            email and password unless you connect Google again.
          </Modal.Content>
          <Modal.Actions>
            <Button
              type="button"
              disabled={busy}
              onClick={() => setShowDisconnectConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              teal
              disabled={busy || passwordRequiredToDisconnect}
              loading={busy}
              onClick={() => {
                setShowDisconnectConfirm(false)
                handleUnlink().catch(() => {
                  /* handled inside handleUnlink */
                })
              }}
            >
              Disconnect
            </Button>
          </Modal.Actions>
        </Modal>
      ) : null}
    </>
  )
}
